<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive, ref } from "vue"
import { LinkIcon } from "@heroicons/vue/24/outline"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable, UiDrawer } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"
import { formatDate, formatDateTime } from "@/utils/date"

const { confirm } = useConfirmation()
const notify = useNotifications()

type StockMovementRow = {
  id: number
  product_id: number
  company: string
  product: string
  movement_type: string
  raw_movement_type: string
  reason: string
  raw_reason: string
  notes: string
  quantity: string
  unit_cost: string
  signed_quantity: string
  reference: string
  reference_type: string
  reference_id: number | null
  reference_purchase_order_id: number | null
  created_by: string
  updated_by: string
  created_at: string
  can_edit: boolean
  can_destroy: boolean
}

type PurchaseOrderDetail = {
  id: number
  company: string
  supplier: string
  status: string
  status_badge: { key: string; tone: string; label: string }
  created_at: string
  expected_at: string | null
  ordered_at: string | null
  received_at: string | null
  cancelled_at: string | null
  created_by: string
  ordered_by: string
  received_by: string
  cancelled_by: string
  notes: string
  total_amount: string
  items_count: number
  items: Array<{ product: string; quantity: string; unit_price: string }>
}

const props = defineProps<{
  stock_movements: StockMovementRow[]
  companies: Array<{ id: number; name: string }>
  movement_types: Array<{ label: string; value: string }>
  balances_by_product: Record<string, string>
  query: {
    q?: string
    company_id?: string
    movement_type?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const columns: UiDataTableColumn[] = [
  { key: "company", label: "Empresa" },
  { key: "product", label: "Produto" },
  { key: "movement_type", label: "Tipo", align: "center" },
  { key: "reason", label: "Motivo" },
  { key: "quantity", label: "Quantidade", align: "right" },
  { key: "signed_quantity", label: "Impacto", align: "right" },
  { key: "balance", label: "Saldo atual", align: "right" },
  { key: "unit_cost", label: "Custo da movimentacao", align: "right" },
  { key: "reference", label: "Referencia" },
  { key: "created_by", label: "Criado por" },
  { key: "created_at", label: "Data", align: "center" },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
  {
    key: "movement_type",
    label: "Tipo",
    options: props.movement_types,
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
  movement_type: props.query.movement_type ?? "",
})

const movementTypeBadgeClassByType: Record<string, string> = {
  in: "border-emerald-200 bg-emerald-50 text-emerald-700",
  out: "border-rose-200 bg-rose-50 text-rose-700",
  adjust: "border-amber-200 bg-amber-50 text-amber-700",
}

const statusBadgeClassByTone: Record<string, string> = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  danger: "bg-rose-50 text-rose-700 border-rose-200",
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
}

const isReferenceDrawerOpen = ref(false)
const selectedReferenceMovement = ref<StockMovementRow | null>(null)
const purchaseOrderDetail = ref<PurchaseOrderDetail | null>(null)
const isLoadingReferenceDetail = ref(false)

function movementTypeBadgeClass(rawMovementType: string) {
  return movementTypeBadgeClassByType[rawMovementType] ?? "border-slate-200 bg-slate-100 text-slate-600"
}

function statusBadgeClass(tone: string): string {
  return statusBadgeClassByTone[tone] ?? statusBadgeClassByTone.neutral
}

async function openPurchaseOrderReferenceDrawer(row: StockMovementRow) {
  if (!row.reference_purchase_order_id) return

  selectedReferenceMovement.value = row
  purchaseOrderDetail.value = null
  isLoadingReferenceDetail.value = true
  isReferenceDrawerOpen.value = true

  try {
    const response = await fetch(`/purchase_orders/${row.reference_purchase_order_id}/detail`, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
      },
    })

    if (response.ok) {
      purchaseOrderDetail.value = await response.json()
    }
  } finally {
    isLoadingReferenceDetail.value = false
  }
}

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    movement_type: String(overrides.movement_type ?? selectedFilters.movement_type ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/stock_movements", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

async function destroyMovement(id: number) {
  const ok = await confirm({
    title: "Excluir movimentacao",
    message: "Deseja excluir esta movimentacao?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/stock_movements/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Movimentacao excluida com sucesso"),
  })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Estoque</h1>
        <p class="mt-2 text-sm text-slate-600">Acompanhe entradas, saidas e ajustes de estoque por produto.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="stock_movements"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/stock_movements/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Nova movimentacao
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <Link
              v-if="(row as StockMovementRow).can_edit"
              :href="`/stock_movements/${(row as StockMovementRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton v-if="(row as StockMovementRow).can_destroy" size="sm" variant="danger" @click="destroyMovement((row as StockMovementRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else-if="column.key === 'balance'">{{ balances_by_product[String((row as StockMovementRow).product_id)] ?? "0" }}</span>
          <span v-else-if="column.key === 'created_at'">{{ formatDate((row as StockMovementRow).created_at) }}</span>
          <span
            v-else-if="column.key === 'movement_type'"
            class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold"
            :class="movementTypeBadgeClass((row as StockMovementRow).raw_movement_type)"
          >
            {{ (row as StockMovementRow).movement_type }}
          </span>
          <div v-else-if="column.key === 'reference'" class="flex items-center gap-2">
            <span>{{ (row as StockMovementRow).reference }}</span>
            <button
              v-if="(row as StockMovementRow).reference_purchase_order_id"
              type="button"
              class="rounded-md border border-slate-300 p-1 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Abrir detalhes do pedido de compra"
              @click="openPurchaseOrderReferenceDrawer(row as StockMovementRow)"
            >
              <LinkIcon class="size-4" />
            </button>
          </div>
          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>

      <UiDrawer v-model="isReferenceDrawerOpen" title="Detalhes do pedido de compra" size="2xl">
        <div v-if="isLoadingReferenceDetail" class="py-8 text-center text-sm text-slate-400">Carregando detalhes do pedido...</div>

        <div v-else-if="purchaseOrderDetail" class="space-y-4">
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-xs uppercase tracking-wide text-slate-500">Pedido de compra</p>
                <h3 class="text-lg font-semibold text-slate-900">#{{ purchaseOrderDetail.id }} - {{ purchaseOrderDetail.supplier }}</h3>
                <p class="mt-1 text-sm text-slate-600">Empresa: {{ purchaseOrderDetail.company }}</p>
              </div>

              <span class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold" :class="statusBadgeClass(purchaseOrderDetail.status_badge.tone)">
                {{ purchaseOrderDetail.status_badge.label }}
              </span>
            </div>

            <div class="mt-4 grid gap-3 md:grid-cols-3">
              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Total</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ purchaseOrderDetail.total_amount }}</p>
              </article>

              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Itens</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ purchaseOrderDetail.items_count }}</p>
              </article>

              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Previsao</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ formatDate(purchaseOrderDetail.expected_at) }}</p>
              </article>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Timeline</h4>
            <dl class="grid gap-2 text-sm sm:grid-cols-2">
              <div><dt class="text-slate-500">Criado</dt><dd class="font-medium text-slate-800">{{ formatDateTime(purchaseOrderDetail.created_at) }} - {{ purchaseOrderDetail.created_by || "Nao informado" }}</dd></div>
              <div><dt class="text-slate-500">Enviado</dt><dd class="font-medium text-slate-800">{{ purchaseOrderDetail.ordered_at ? `${formatDateTime(purchaseOrderDetail.ordered_at)} - ${purchaseOrderDetail.ordered_by || "Nao informado"}` : "Pendente" }}</dd></div>
              <div><dt class="text-slate-500">Recebido</dt><dd class="font-medium text-slate-800">{{ purchaseOrderDetail.received_at ? `${formatDateTime(purchaseOrderDetail.received_at)} - ${purchaseOrderDetail.received_by || "Nao informado"}` : "Pendente" }}</dd></div>
              <div><dt class="text-slate-500">Cancelado</dt><dd class="font-medium text-slate-800">{{ purchaseOrderDetail.cancelled_at ? `${formatDateTime(purchaseOrderDetail.cancelled_at)} - ${purchaseOrderDetail.cancelled_by || "Nao informado"}` : "Nao cancelado" }}</dd></div>
            </dl>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Itens do pedido</h4>
            <div class="overflow-hidden rounded-md border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm">
                <thead class="bg-slate-50 text-slate-600">
                  <tr>
                    <th class="px-3 py-2 text-left font-medium">Produto</th>
                    <th class="px-3 py-2 text-right font-medium">Quantidade</th>
                    <th class="px-3 py-2 text-right font-medium">Preco unitario</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr v-if="purchaseOrderDetail.items.length === 0">
                    <td colspan="3" class="px-3 py-4 text-center text-slate-500">Sem itens no pedido.</td>
                  </tr>
                  <tr v-for="(item, index) in purchaseOrderDetail.items" v-else :key="`po-item-${index}`">
                    <td class="px-3 py-2 text-slate-800">{{ item.product }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ item.quantity }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ item.unit_price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div v-else class="py-8 text-center text-sm text-slate-400">Nao foi possivel carregar o pedido de compra.</div>
      </UiDrawer>
    </div>
  </AppShell>
</template>

